/**
 * Upload API — 文件上传（MinIO S3 预签名 PUT）
 *
 * 流程：
 *   1. POST /discourse/api/v1/upload/presign → 获取 uploadUrl + accessUrl + formData
 *   2. PUT uploadUrl（multipart/form-data，携带 policy 签名字段 + file）
 *   3. 使用 accessUrl 作为最终可访问地址
 */

import { useEffect, useState } from "react";
import type {
  PresignUploadRequest,
  PresignUploadResponse,
} from "@zrt-ai-office/types";
import { apiFetch } from "./client";

/* ── 文件后缀 → fileType 映射 ── */

const IMAGE_EXTS = new Set([
  ".jpg",
  ".jpeg",
  ".png",
  ".gif",
  ".webp",
  ".svg",
  ".bmp",
  ".ico",
  ".avif",
]);
const VIDEO_EXTS = new Set([
  ".mp4",
  ".webm",
  ".ogg",
  ".mov",
  ".avi",
  ".mkv",
  ".flv",
  ".wmv",
]);
const ARCHIVE_EXTS = new Set([
  ".zip",
  ".tar",
  ".gz",
  ".tgz",
  ".bz2",
  ".xz",
  ".rar",
  ".7z",
]);

/**
 * 根据文件名后缀推断 fileType
 */
export function inferFileType(
  filename: string,
): "image" | "video" | "file" | "archive" {
  const name = filename.toLowerCase();
  // 处理复合后缀（如 .tar.gz / .tar.bz2 / .tar.xz）
  if (/\.tar\.(gz|bz2|xz)$/.test(name)) return "archive";
  const dotIdx = name.lastIndexOf(".");
  if (dotIdx === -1) return "file";
  const ext = name.slice(dotIdx);
  if (IMAGE_EXTS.has(ext)) return "image";
  if (VIDEO_EXTS.has(ext)) return "video";
  if (ARCHIVE_EXTS.has(ext)) return "archive";
  return "file";
}

/**
 * 获取预签名上传 URL
 */
export async function getPresignUrl(
  req: PresignUploadRequest,
): Promise<PresignUploadResponse> {
  return apiFetch<PresignUploadResponse>("/discourse/api/v1/upload/presign", {
    method: "POST",
    body: req,
  });
}

/**
 * 通用文件上传（PUT 直传）：预签名 → PUT binary → 返回访问地址
 *
 * @param file - 要上传的文件，fileType 根据文件名后缀自动推断
 * @returns accessUrl - 上传完成后的公开访问地址
 */
export async function uploadFile(file: File): Promise<string> {
  // Step 1: 根据文件名后缀推断 fileType，获取预签名 URL
  const { uploadUrl, accessUrl } = await getPresignUrl({
    fileType: inferFileType(file.name),
    filename: file.name,
    fileSize: file.size,
  });

  // Step 2: 用 PUT + binary body 直传（不设额外 header，避免签名不匹配）
  const res = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
  });

  if (!res.ok) {
    throw new Error(`文件上传失败: ${res.status} ${res.statusText}`);
  }

  return accessUrl;
}
